"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PojoSet = void 0;
/**
 * Create a a PojoSet from an array of values.
 * @param values values to populate the set with
 * @returns a PojoSet containing the values
 */
function from(values) {
    return values.reduce(function (acc, next) {
        acc[next] = true;
        return acc;
    }, {});
}
/**
 * Check if an value is in a PojoSet.
 *
 * @param set A PojoSet
 * @param value The value to check existence of
 * @returns true if the value exists in the set, false otherwise.
 */
function has(set, value) {
    return !!set[value];
}
/**
 * Convert a PojoSet to an array of values.
 * @param set A PojoSet
 * @returns an array containing all the values in the set.
 */
function toArray(set) {
    var keys = Object.keys(set);
    // For maximum compatibility, filter on the values, in case the user has set some fields to false manually.
    return keys.filter(function (k) { return has(set, k); });
}
/**
 * Count the number of values contained in a PojoSet.
 * @param set A PojoSet
 * @returns the number of values in the PojoSet
 */
function size(set) {
    return toArray(set).length;
}
/**
 * Create a PojoSet from the values of a Typescript Enum.
 *
 * @param enumObj A typescript `enum` object
 * @returns A PojoSet of the enum values
 */
function fromEnum(enumObj) {
    return from(Object.keys(enumObj)
        // Filter out numeric value entries from
        // numeric & hybrid TS enums
        .filter(function (k) { return isNaN(parseInt(k, 10)); })
        .map(function (k) { return enumObj[k]; }));
}
/**
 * Add an item to a PojoSet, immutably.
 *
 * @param set An existing PojoSet
 * @param value The value to add
 * @returns A new PojoSet contianing the original set and the given value.
 */
function add(set, value) {
    var _a;
    return __assign(__assign({}, set), (_a = {}, _a[value] = true, _a));
}
/**
 * Remove an item from a PojoSet, immutably.
 *
 * @param set An existing PojoSet
 * @param value The value to remove
 * @returns A new PojoSet contianing the original set minus the given value.
 */
function remove(set, value) {
    // Extracting the the value from the PojoSet object via destructuring
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    var _a = set, _b = value, removed = _a[_b], remaining = __rest(_a, [typeof _b === "symbol" ? _b : _b + ""]);
    return remaining;
}
function toggle(set, value, enable) {
    if (enable) {
        return add(set, value);
    }
    else {
        // If `U` is not part of `T` this should technically be a No-Op.
        return remove(set, value);
    }
}
/**
 * Create an empty PojoSet of the given type.
 *
 * @returns A new, empty PojoSet
 */
function empty() {
    return {};
}
/**
 * Create a PojoSet from the union of two PojoSets.
 *
 * @param a A PojoSet
 * @param b A PojoSet
 * @returns A new PojoSet containing the values from both input sets.
 */
function union(a, b) {
    return __assign(__assign({}, a), b);
}
/**
 * Create a PojoSet from the set difference of two PojoSets
 *
 * @param a A PojoSet
 * @param b A PojoSet
 * @returns A new PojoSet containing the values from the first set, minus the values shared by both sets.
 */
function difference(a, b) {
    return from(
    // Remove all items in A that exist in B
    toArray(a).filter(function (i) { return !has(b, i); }));
}
/**
 * Create a PojoSet from the set intersection of two PojoSets
 *
 * @param a A PojoSet
 * @param b A PojoSet
 * @returns A new PojoSet containing only the values that exist in both input sets.
 */
function intersection(a, b) {
    return from(
    // Only keep items that exist in both A and B
    toArray(a).filter(function (i) { return has(b, i); }));
}
exports.PojoSet = {
    add: add,
    remove: remove,
    toggle: toggle,
    has: has,
    size: size,
    from: from,
    toArray: toArray,
    fromEnum: fromEnum,
    union: union,
    empty: empty,
    plus: union,
    difference: difference,
    subtract: difference,
    intersection: intersection,
};
